<template>
  <b-col class="m-auto">
    <slot name="logo"></slot>
    <h4 class="text-muted pb-4" v-if="!loading">
      {{ labels.confirmAccount.title }}
    </h4>
    <div v-if="loading" class="loading">
      <loading />
      <p>{{ resources.page.login.new.confirmEmailPage.loading }}</p>
    </div>
    <b-form v-else @submit.prevent="doConfirmAccount" ref="form">
      <CustomInput
        type="text"
        :label="labels.input.code"
        @update="updateConfirmAccountForm('code', $event.value)"
      />
      <b-tooltip target="text">
        <div class="tooltip-info">
          {{ labels.input.codeHelp }}
        </div>
      </b-tooltip>
      <CustomInput
        :labelUp="true"
        :placeholder="labels.confirmAccount.birthDatePlaceholder"
        :hasMask="true"
        name="date"
        :label="labels.confirmAccount.birthDate"
        @update="updateConfirmAccountForm('birthDate', $event.value)"
      />

      <b-form-group v-if="secretQuestions">
        <b-form-select
          id="secret-question-form-select"
          class="secret-question-select"
          size="lg"
          required
          @input="changeSecretQuestion($event)"
        >
          <option
            v-for="question in secretQuestions"
            :key="question.id"
            :value="question.id"
          >
            {{ question.label }}
          </option>
        </b-form-select>
        <label for="secret-question-form-select" class="custom-input-label">
          {{ labels.confirmAccount.secretQuestion }}
        </label>

        <label class="mdi mdi-chevron-down select-icon"></label>
      </b-form-group>

      <CustomInput
        v-if="secretQuestions"
        name="secret-question-answer"
        type="text"
        class="secret-question-answer"
        :label="labels.confirmAccount.secretQuestionAnswer"
        @update="updateConfirmAccountForm('secretQuestionAnswer', $event.value)"
      />

      <CustomInput
        type="password"
        hasPasswordConfirm="true"
        :passwordRules="passwordRules"
        :title="labels.input.password"
        :label="labels.input.password"
        :labelWrong="labels.input.password"
        :confirmTitle="labels.input.passwordConfirmWarning"
        :confirmLabel="labels.input.passwordConfirm"
        :confirmLabelWrong="labels.input.passwordConfirmWrong"
        @update="updateConfirmAccountForm('password', $event.value)"
      />
      <b-row v-if="confirmAccount.message" align-h="center" class="mt-2">
        <span
          id="exception-message"
          class="text-secondary"
          v-if="confirmAccount.message === 'CONFIRMED'"
        >
          {{ labels.confirmAccount.confirmedAccount1 }}
          <b-link :to="{ name: 'login.auth' }">
            {{ labels.confirmAccount.confirmedAccount2 }}
          </b-link>
          {{ labels.confirmAccount.confirmedAccount3 }}
        </span>
        <span id="exception-message" class="text-secondary" v-else>
          {{ confirmAccount.message }}
        </span>
      </b-row>
      <small class="font-weight-normal">
        {{ labels.termsAndPrivacy.partOne }}
        <strong>"{{ labels.confirmAccount.register }}"</strong>
        {{ labels.termsAndPrivacy.partTwo }}
        <a :href="labels.termsAndPrivacy.link.privacy" target="_blank">
          {{ labels.termsAndPrivacy.partThree }}
        </a>
        {{ labels.termsAndPrivacy.partFour }}
        <a :href="labels.termsAndPrivacy.link.terms" target="_blank">
          {{ labels.termsAndPrivacy.partFive }}
        </a>
        .
      </small>
      <Recaptcha ref="recaptcha" @verify="recaptchaVerify">
        <b-button
          type="submit"
          :disabled="submitInProgress"
          variant="quartenary"
          class="col-12 btn-account"
        >
          <i
            class="mdi mdi-spin pr-2"
            :class="{ 'mdi-loading': submitInProgress }"
          ></i>
          {{ labels.confirmAccount.register }}
        </b-button>
      </Recaptcha>
    </b-form>
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import { getUser } from '@/service/user'
import ability from '@/helpers/ability'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import Recaptcha from '@/components/Shared/Recaptcha.vue'
import Loading from '@/components/Shared/Loading.vue'

export default {
  components: {
    CustomInput,
    Recaptcha,
    Loading,
  },

  data() {
    return {
      loading: false,
      passwordRules: undefined,
      secretQuestions: undefined,
      submitInProgress: false,
    }
  },

  mounted() {
    this.$nextTick(() => {
      const { token } = this.$route.params

      this.loading = true
      this.$store
        .dispatch('user/loadPasswordRules', {
          token,
          action: 'confirm-account',
        })
        .then(async ({ data: { hasSecretQuestion, passwordRules } }) => {
          if (hasSecretQuestion) {
            const { data } = await this.$store.dispatch(
              'user/loadSecretQuestions'
            )
            this.secretQuestions = data
          }

          this.passwordRules = passwordRules
          this.loading = false

          this.updateConfirmAccountForm('token', this.$route.params.token)
        })
        .catch(error => {
          this.$store.commit('updateMessage', error.response.data.message)
          this.loading = false
        })
    })
  },

  computed: {
    ...mapGetters({
      confirmAccount: 'confirmAccount',
      resources: 'resources',
    }),

    labels: function() {
      return {
        confirmAccount: this.resources.page.login.confirmAccount,
        input: this.resources.general.input,
        termsAndPrivacy: this.resources.general.termsAndPrivacy,
      }
    },
  },

  methods: {
    changeSecretQuestion(value) {
      this.updateConfirmAccountForm('secretQuestion', value)
    },

    recaptchaVerify(value) {
      this.updateConfirmAccountForm('googleRecaptcha', value)
      this._doConfirmAccount()
    },

    updateConfirmAccountForm(property, value) {
      this.$store.commit('updateConfirmAccountForm', { property, value })
    },

    async redirectAfterConfirm() {
      try {
        const redirectNew = new URLSearchParams(
          document.location.search.substring(1)
        ).get('redirect-new')
        if (!redirectNew) throw new Error()

        window.location.pathname = '/dashboard'
      } catch (error) {
        const redirectAfterLogin = new URLSearchParams(
          document.location.search.substring(1)
        ).get('redirect')

        window.location = redirectAfterLogin || '/survey'
      }
    },

    doConfirmAccount() {
      if (this.submitInProgress) return false

      if (!this.$refs.form.checkValidity()) {
        this.$refs.form.reportValidity()
        this.resetRecaptcha()
        return false
      }

      this.$refs.recaptcha.execute()
    },

    _doConfirmAccount() {

      this.submitInProgress = true
      this.$store.commit('updateMessage', null)
      this.$store
        .dispatch('confirmAccount')
        .then(response => {
          this.submitInProgress = false

          this.$store.dispatch('user/saveUserData', response)
          this.$root.$emit('login', getUser())
          this.$ability.update(ability().rules)

          this.redirectAfterConfirm()
        })
        .catch(error => {
          const { data } = error.response
          if (
            data.statusCode === 400 &&
            data.validation.keys.includes('birthDate')
          ) {
            this.$store.commit(
              'updateMessage',
              this.labels.confirmAccount.birthDateInvalid
            )
          } else {
            this.$store.commit('updateMessage', error.response.data.message)
          }
          this.submitInProgress = false
          this.resetRecaptcha()
        })
    },

    resetRecaptcha() {
      this.$refs.recaptcha.resetRecaptcha()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.btn-account {
  margin-top: 2.5rem !important;
}

.loading {
  margin: 0 auto;
  text-align: center;
}

.secret-question-select {
  cursor: pointer;
}

.secret-question-answer {
  padding-top: 1.2rem !important;
}

/deep/ .custom-input-label {
  font-size: 12px;
  color: map-get($theme-colors, 'gray03') !important;
  top: -18px;
}

.select-icon {
  top: 2px !important;
  right: 6px !important;
}
</style>
